import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatNumber, parseQty, formatQty } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Badge } from "@/components/ui-kit";
import BulkImport from "@/components/BulkImport";
import MasterCombo from "@/components/MasterCombo";
import { DamageReportModal } from "@/components/stock/DamageReportModal";
import { DamageHistoryModal } from "@/components/stock/DamageHistoryModal";
import { StockQuickAdjust } from "@/components/stock/StockQuickAdjust";
import { StockInModal } from "@/components/stock/StockInModal";
import { useTableFilter } from "@/hooks/useTableFilter";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, AlertTriangle, PackagePlus } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const blank = { material_name: "", unit: "", quantity: 0, low_threshold: 10, category: "", code: "" };

export default function Stock() {
  const { t } = useLang();
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [edit, setEdit] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [damageStock, setDamageStock] = useState(null); // when set, opens damage report modal
  const [historyStock, setHistoryStock] = useState(null); // damage history viewer
  const [stockInRow, setStockInRow] = useState(null); // Iter v34f (#5) — Barang Masuk modal
  // Iter v34o (#2) — Unified period dropdown: Hari Ini / Minggu Ini / Bulan Ini /
  // Tahun Ini / Rentang Custom. When "custom" selected, show start/end date inputs.
  const _now = new Date();
  const _todayISO = (d) => d.toISOString().slice(0, 10);
  const _startOfWeekISO = (d) => {
    const day = d.getDay() || 7;
    const x = new Date(d);
    x.setDate(d.getDate() - (day - 1));
    return x.toISOString().slice(0, 10);
  };
  const _startOfMonthISO = (d) => new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const _startOfYearISO = (d) => new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
  const [period, setPeriod] = useState("month"); // day | week | month | year | custom
  const [startDate, setStartDate] = useState(_startOfMonthISO(_now));
  const [endDate, setEndDate] = useState(_todayISO(_now));
  const applyPreset = (p) => {
    setPeriod(p);
    const t0 = new Date();
    if (p === "day") setStartDate(_todayISO(t0));
    else if (p === "week") setStartDate(_startOfWeekISO(t0));
    else if (p === "month") setStartDate(_startOfMonthISO(t0));
    else if (p === "year") setStartDate(_startOfYearISO(t0));
    setEndDate(_todayISO(t0));
  };
  const { filtered: textFiltered, FilterBar } = useTableFilter(rows, ["material_name", "unit", "category"], "Cari material…");
  const filtered = categoryFilter ? textFiltered.filter((r) => (r.category || "") === categoryFilter) : textFiltered;
  const { pageItems, Pagination } = usePagination(filtered, { perPage: 14 });

  const load = () => {
    // Iter v34o (#2) — Backend supports custom start_date+end_date OR year+month+day.
    // We always send start_date+end_date so all presets use the same path.
    const params = { start_date: startDate, end_date: endDate };
    return Promise.all([api.get("/stocks", { params }), api.get("/masters/categories")]).then(([s, c]) => {
      setRows(s.data); setCategories(c.data);
    });
  };
  useEffect(() => { load(); }, [startDate, endDate]);

  const submit = async () => {
    try {
      const payload = { ...form, quantity: parseQty(form.quantity), low_threshold: parseQty(form.low_threshold) };
      if (edit && edit.id) {
        await api.patch(`/stocks/${edit.id}`, payload);
      } else {
        await api.post("/stocks", payload);
      }
      toast.success("Stok tersimpan"); setOpen(false); setForm(blank); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/stocks/${id}`); load(); };

  // Iter v34aw · Bento — compute stats
  const lowStockCount = rows.filter((r) => {
    const q = parseFloat(r.quantity) || 0;
    const th = parseFloat(r.low_threshold) || 0;
    return th > 0 && q <= th;
  }).length;
  const totalDamaged = rows.reduce((s, r) => s + (parseFloat(r.damaged_qty) || 0), 0);
  const totalLost = rows.reduce((s, r) => s + (parseFloat(r.lost_qty) || 0), 0);
  const totalQtyOut = rows.reduce((s, r) => s + (parseFloat(r.qty_out) || 0), 0);

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Master · Inventory"
        title={t("stock_items")}
        description="Pantau stok material. Notifikasi akan muncul di dashboard saat stok mencapai batas minimum."
        action={<div className="flex items-center gap-2">{hasPerm("stock.create") && <BulkImport kind="stocks" onDone={load} />}{hasPerm("stock.create") && <Button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }} data-testid="add-stock-btn"><Plus className="h-4 w-4" />{t("input_stock")}</Button>}</div>}
      />

      {/* Bento KPI hero */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Material</div>
          <div className="font-display text-3xl mt-1 text-stone-900">{rows.length}</div>
        </div>
        <div className={`rounded-2xl border p-4 ${lowStockCount > 0 ? "bg-red-50/60 border-red-200" : "bg-emerald-50/60 border-emerald-100"}`}>
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${lowStockCount > 0 ? "text-red-700" : "text-emerald-700"}`}>Stok Rendah</div>
          <div className={`font-display text-3xl mt-1 ${lowStockCount > 0 ? "text-red-800" : "text-stone-900"}`}>{lowStockCount}</div>
        </div>
        <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-4">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Rusak · Periode</div>
          <div className="font-mono-num text-xl mt-1 text-stone-900">{formatNumber(totalDamaged)}</div>
        </div>
        <div className="rounded-2xl bg-blue-50/60 border border-blue-100 p-4">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Qty Keluar · Periode</div>
          <div className="font-mono-num text-xl mt-1 text-stone-900">{formatNumber(totalQtyOut)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {FilterBar}
        <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="!w-auto" data-testid="stock-category-filter">
          <option value="">Semua Kategori</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </Select>
      </div>

      {/* Iter v34o (#2) — Unified period filter dropdown + custom range */}
      <Card className="p-3 bg-stone-50/50">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-semibold uppercase tracking-wider text-stone-600 mr-1">Filter Periode (Qty Keluar, Stok Rusak & Hilang):</span>
          <Select value={period} onChange={(e) => applyPreset(e.target.value)} className="!w-auto !text-xs" data-testid="stock-filter-period">
            <option value="day">Hari Ini</option>
            <option value="week">Minggu Ini</option>
            <option value="month">Bulan Ini</option>
            <option value="year">Tahun Ini</option>
            <option value="custom">Rentang Custom</option>
          </Select>
          {period === "custom" && (
            <>
              <Field className="!mb-0">
                <DatePicker value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!text-xs !h-8" data-testid="stock-filter-start" />
              </Field>
              <span className="text-stone-400">s.d.</span>
              <Field className="!mb-0">
                <DatePicker value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!text-xs !h-8" data-testid="stock-filter-end" />
              </Field>
            </>
          )}
          {period !== "custom" && (
            <span className="text-stone-500 font-mono text-[11px]" data-testid="stock-filter-range-display">
              {startDate} → {endDate}
            </span>
          )}
          <Button variant="ghost" className="!py-1 !px-2 !text-xs" onClick={() => applyPreset("month")} data-testid="stock-filter-reset">Reset (Bulan Ini)</Button>
        </div>
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b-2 border-stone-300 text-left text-[10px] font-bold uppercase tracking-widest text-stone-700">
              <th className="py-3 px-4 w-24">Kode</th>
              <th className="py-3 px-4">{t("material_name")}</th>
              <th className="py-3 px-4 w-32">Kategori</th>
              <th className="py-3 px-4 w-20">{t("unit")}</th>
              <th className="py-3 px-4 text-right w-28">{t("quantity")}</th>
              <th className="py-3 px-4 text-right w-28">Qty Keluar</th>
              <th className="py-3 px-4 text-right w-28">Stok Rusak</th>
              <th className="py-3 px-4 text-right w-28">Stok Hilang</th>
              <th className="py-3 px-4 w-24">Status</th>
              <th className="py-3 px-4 text-right w-28">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={10} className="py-12 text-center text-stone-400">Tidak ada stok.</td></tr>}
            {pageItems.map((r, idx) => {
              const qty = parseFloat(r.quantity) || 0;
              const thr = parseFloat(r.low_threshold) || 0;
              const low = qty <= thr;
              const warning = !low && thr > 0 && qty <= thr * 1.5;
              const dmg = parseFloat(r.damaged_qty) || 0;
              const lost = parseFloat(r.lost_qty) || 0;
              const qtyOut = parseFloat(r.qty_out) || 0;
              return (
                <tr key={r.id} className={`border-b border-stone-100 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-stone-50/30"} hover:bg-terra-50/40`} data-testid={`stock-row-${idx}`}>
                  <td className="py-3 px-4 font-mono text-xs text-stone-700">{r.code || <span className="text-stone-300">—</span>}</td>
                  <td className="py-3 px-4 font-semibold text-stone-900">{r.material_name}</td>
                  <td className="py-3 px-4">{r.category ? <Badge tone="terra">{r.category}</Badge> : <span className="text-stone-300">—</span>}</td>
                  <td className="py-3 px-4 text-stone-600 uppercase text-xs">{r.unit}</td>
                  <td className={`py-3 px-4 text-right font-mono-num font-semibold ${low ? "text-red-700" : warning ? "text-amber-700" : "text-stone-900"}`} title={thr > 0 ? `Batas minimum: ${formatNumber(thr, 0)} ${r.unit}` : ""}>
                    {formatQty(qty)}
                  </td>
                  <td className={`py-3 px-4 text-right font-mono-num ${qtyOut > 0 ? "text-stone-700 font-semibold" : "text-stone-400"}`} data-testid={`stock-qty-out-${idx}`} title="Pengiriman / barang keluar pada periode terpilih">
                    {qtyOut > 0 ? formatQty(qtyOut) : "—"}
                  </td>
                  <td className={`py-3 px-4 text-right font-mono-num ${dmg > 0 ? "text-red-700 font-semibold cursor-pointer hover:underline" : "text-stone-400"}`} data-testid={`stock-damaged-qty-${idx}`} onClick={() => dmg > 0 && setHistoryStock(r)} title={dmg > 0 ? "Klik untuk lihat / edit laporan kerusakan" : ""}>
                    {dmg > 0 ? formatQty(dmg) : "—"}
                  </td>
                  <td className={`py-3 px-4 text-right font-mono-num ${lost > 0 ? "text-orange-700 font-semibold cursor-pointer hover:underline" : "text-stone-400"}`} data-testid={`stock-lost-qty-${idx}`} onClick={() => lost > 0 && setHistoryStock(r)} title={lost > 0 ? "Klik untuk lihat / edit laporan hilang" : ""}>
                    {lost > 0 ? formatQty(lost) : "—"}
                  </td>
                  <td className="py-3 px-4">{low ? <Badge tone="danger">{t("low_stock")}</Badge> : warning ? <Badge tone="warn">Hampir Habis</Badge> : <Badge tone="success">OK</Badge>}</td>
                  <td className="py-3 px-4 text-right">
                    {/* Iter v34f (#5) — Barang Masuk dengan foto SJ. Iter v34k (#3) — perm terpisah `stock.stockin`. */}
                    {hasPerm("stock.stockin") && (
                      <button onClick={() => setStockInRow(r)} className="text-emerald-600 hover:text-emerald-800 px-2 transition-colors" title="Catat Barang Masuk" data-testid={`stock-in-btn-${idx}`}>
                        <PackagePlus className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {hasPerm("damage.create") && (
                      <button onClick={() => setDamageStock(r)} className="text-amber-600 hover:text-amber-800 px-2 transition-colors" title="Lapor Material Rusak" data-testid={`stock-report-damage-${idx}`}>
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {hasPerm("stock.edit") && <button onClick={() => { setEdit(r); setForm({ ...blank, ...r }); setOpen(true); }} className="text-stone-500 hover:text-terra-700 px-2 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>}
                    {hasPerm("stock.delete") && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {Pagination}

      <Modal open={open} onClose={() => setOpen(false)} title={t("input_stock")}>
        <div className="space-y-4">
          <Field label={t("material_name")} required>
            <Input value={form.material_name} onChange={(e) => setForm({ ...form, material_name: e.target.value })} data-testid="form-stock-name" />
          </Field>
          <Field label="Kode" hint="Akan muncul di dropdown pembuatan Surat Jalan untuk auto-deduct">
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="contoh: BTC-01" data-testid="form-stock-code" />
          </Field>
          <Field label="Kategori">
            <MasterCombo source="categories" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="delivery, service…" data-testid="form-stock-category" />
          </Field>
          <Field label={t("unit")} required>
            <MasterCombo source="units" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} />
          </Field>
          <Field label={t("quantity")}>
            <Input className="font-mono-num" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </Field>
          <Field label={t("threshold")}>
            <Input className="font-mono-num" value={form.low_threshold} onChange={(e) => setForm({ ...form, low_threshold: e.target.value })} />
          </Field>
          {edit && (
            <div className="md:col-span-2 border-t border-stone-200 pt-4 mt-2 space-y-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Penyesuaian Stok Cepat</p>
              <p className="text-[11px] text-stone-500">Isi salah satu nilai lalu klik tombol untuk menambah / mengurangi stok manual. Tercatat di Log Stok.</p>
              <StockQuickAdjust stockId={edit.id} unit={edit.unit} onDone={() => { load(); setOpen(false); }} />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-stock-btn">{t("save")}</Button>
          </div>
        </div>
      </Modal>

      <DamageReportModal
        open={!!damageStock}
        stock={damageStock}
        onClose={() => setDamageStock(null)}
        onSuccess={load}
      />

      <DamageHistoryModal
        open={!!historyStock}
        stock={historyStock}
        onClose={() => setHistoryStock(null)}
        onChanged={load}
      />

      {/* Iter v34f (#5) — Barang Masuk modal */}
      <StockInModal
        stock={stockInRow}
        onClose={() => setStockInRow(null)}
        onDone={load}
      />
    </div>
    </div>
  );
}
